import {
  Flex,
  Text,
  color,
  spacing,
  typography,
  useColorMode,
  useIsMobileView,
} from 'deepstash-ui';
import dynamic from 'next/dynamic';
import SourceProvider from 'providers/source/SourceProvider';
import React from 'react';
import NewCollectionCarousel from 'src/components/collection/NewCollectionCarousel';
import Layout from 'src/components/layout/Layout';
import { LayoutSection } from 'src/components/layout/Layout.types';
import SubsectionHeader from 'src/components/metacontent/components/SubsectionHeader';
import useProfile from 'src/providers/hooks/useProfile';
import { PageMetadata } from 'types';
import { ArticleStatus } from 'types/enums';
import { Idea, Source } from 'types/models';
import { GAP_SIZE, GAP_SIZE_REM, SECTION_GAP_REM, slugify } from 'utils/global';
import commonStrings from 'utils/strings/commonStrings';
import CollectionInHeader from './components/CollectionInHeader';
import IdeaPageMetacontent from './components/IdeaPageMetacontent';
import SimilarIdeasList from './components/SimilarIdeasList';
import SourceCardIdeas from './components/SourceCardIdeas';
import SourceSplashscreenBackground from './components/SourceSplashscreenBackground';
import SourceTitleByType from './components/header/SourceTitleByType';
import FullWidthSectionWrapper from 'src/components/layout/components/FullWidthSectionWrapper';
import { UNSORTED_TOPICS } from 'utils/metatopics.constants';

const PrivateSourcePage = dynamic(
  () => import('./components/SourcePrivatePage'),
);

interface IdeaPageProps {
  source: Source;
  similarIdeas?: Idea[];
  pageMetadata: PageMetadata;
  sectionBefore?: LayoutSection;
}

const IdeaPage: React.FC<IdeaPageProps> = ({
  source,
  similarIdeas,
  pageMetadata,
  sectionBefore,
}) => {
  const { profile } = useProfile();
  const isMobileView = useIsMobileView();
  const { colorMode } = useColorMode();

  const isOwnArticle = profile?.id === source.userData.id;
  const isDraft = source.status === ArticleStatus.DRAFT;

  const firstIdea = source.ideas[0];

  if (isDraft && !isOwnArticle) {
    return (
      <Layout
        pageMetadata={pageMetadata}
        sections={[{ content: <PrivateSourcePage /> }]}
      />
    );
  }

  const sourceIdeasSection: LayoutSection = {
    content: (
      <>
        {isMobileView && (
          <>
            <SubsectionHeader textTransform="uppercase">
              {commonStrings.ideaCuratedFrom}
            </SubsectionHeader>
            <SourceTitleByType
              source={source}
              size={'sm'}
              withLink={true}
              mb={GAP_SIZE_REM}
            />
          </>
        )}
        <SourceCardIdeas />
      </>
    ),
    metaContent: <IdeaPageMetacontent />,
    metaContentTopOffset: 0,
    metaContentStickyOffset: GAP_SIZE * 2,
    metaContentColumnStart: 0,
  };

  const similarIdeasSection: LayoutSection = {
    content: (
      <Flex
        flexDirection="column"
        width={'100%'}
        {...(isMobileView ? { mt: 16 } : { mt: 48 })}
      >
        {source.seo?.recommended_collection !== undefined ? (
          <FullWidthSectionWrapper
            containerProps={{
              flexDirection: 'column',
              gridGap: spacing.XS.rem,
            }}
          >
            <SubsectionHeader>
              The idea is part of this collection:
            </SubsectionHeader>
            <CollectionInHeader
              collection={source.seo.recommended_collection}
              topic={source.ideas[0].hashtags?.[0] ?? ''}
              mb={SECTION_GAP_REM}
            />
          </FullWidthSectionWrapper>
        ) : null}
        {source.seo?.similar_collections !== undefined &&
        source.seo.similar_collections.length > 0 ? (
          <>
            <Text
              {...typography.primaryHeadingFonts.medium.bold}
              color={color[colorMode].text}
              {...(isMobileView ? { px: 16 } : {})}
            >
              Related collections
            </Text>
            <NewCollectionCarousel
              collections={source.seo.similar_collections?.slice(0, 4)}
              justifyContent="flex-start"
              mt={spacing.M.rem}
              pl={spacing.M.rem}
            />
          </>
        ) : null}
        <SimilarIdeasList
          similarIdeas={similarIdeas}
          originalIdeaTitle={
            source.ideas[0].title ?? source.ideas[0].authorName
          }
          {...(isMobileView ? { px: 16 } : {})}
        />
      </Flex>
    ),
    isFullscreen: isMobileView ? true : false,
  };

  return (
    <SourceProvider source={source}>
      <SourceSplashscreenBackground
        zIndex={-1}
        themeColor={source.imageColor}
      />

      <Layout
        pageMetadata={pageMetadata}
        sections={
          sectionBefore
            ? [sectionBefore, sourceIdeasSection, similarIdeasSection]
            : [sourceIdeasSection, similarIdeasSection]
        }
        breadcrumbs={
          !!source.ideas[0]?.hashtags?.length
            ? [
                {
                  title: 'Home',
                  link: '/',
                },
                {
                  title:
                    UNSORTED_TOPICS[source.ideas[0]?.hashtags?.[0]]?.title ??
                    source.ideas[0]?.hashtags?.[0],
                  link: '/topic/' + source.ideas[0]?.hashtags?.[0],
                },
                {
                  title: source.title,
                  link: `/article/${source.id}/${slugify(source.title)}`,
                },
                {
                  title: !!firstIdea.title?.length
                    ? firstIdea.title
                    : firstIdea.authorName
                    ? 'Quote by ' + firstIdea.authorName
                    : 'Untitled Idea',
                  link: `/idea/${firstIdea.id}/${slugify(
                    firstIdea.title,
                    firstIdea.content,
                  )}`,
                },
              ]
            : undefined
        }
        isShortProAd
        showFooterPrices={false}
      />
    </SourceProvider>
  );
};

export default IdeaPage;
